import { Button, Col, Input, InputRef, Row } from "antd";
import { SearchOutlined } from '@ant-design/icons';
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

import { ReactComponent as BackIcon } from 'assets/svg/navigate-before.svg';
import { User } from 'services/types/ProfileTypes';
import { useHotCampaigns, usePrivateCampaigns } from "utils/apis";
import { usePeopleToFollow } from 'services/UserService';
import CampaignList from "./CampaignList";
import UserList from "./UserList";
import SearchFilter from "./SearchFilter";
import { AppContext } from "context/AppContext";
import { followHandler as followHandlerUtil } from "utils/followUtils";

interface SearchProps {
  closeHandler?: () => void;
  followHandler?: (user: User) => Promise<void>;
}

const Search: React.FC<SearchProps> = ({
  closeHandler,
  followHandler,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<InputRef>(null);
  const [keyword, setKeyword] = useState('');
  const debouncedSetKeyword = useMemo(() => debounce(setKeyword, 500), [setKeyword]);
  const { authentication } = useContext(AppContext);
  const { authKey } = authentication;

  // Load data for the three sections
  const { data: hotCampaigns, isLoading: isHotCampaignsLoading } = useHotCampaigns();
  const { data: privateCampaigns, isLoading: isPrivateCampaignsLoading } = usePrivateCampaigns();
  const { data: peopleToFollow, isLoading: isPeopleToFollowLoading } = usePeopleToFollow(authKey);
  const { api } = useContext(AppContext);

  const sharedFollowHandler = useCallback(
    async (user: User) => {
      await followHandlerUtil(api, user);
    },
    [api]
  );

  const effectiveFollowHandler = followHandler || sharedFollowHandler;

  useEffect(() => {
    // wait for animation to end
    setTimeout(() => inputRef.current?.focus({ cursor: 'start' }), 300);
  }, [inputRef]);

  return (
    <>
      <Row
        align={'middle'}
        style={{ position: 'sticky', top: 0, padding: '24px 0 12px', backgroundColor: '#fff', zIndex: 20 }}
      >
        <Col span={4}>
          <Button
            type="text"
            icon={<BackIcon />}
            onClick={closeHandler} />
        </Col>

        <Col span={16}>
          <Input
            placeholder={t('search.searchPlaceholder')}
            prefix={<SearchOutlined />}
            style={{ borderRadius: 10 }}
            onChange={(e) => debouncedSetKeyword(e.target.value)}
            ref={inputRef}
            allowClear
          />
        </Col>
      </Row>

      {keyword === '' && (
        <Row gutter={[0, 32]}>
          {/* Hot Campaigns Section */}
          <Col span={24}>
            <CampaignList
              title={t('search.hot_campaigns')}
              isLoading={isHotCampaignsLoading}
              campaigns={hotCampaigns}
              closeHandler={closeHandler}
            />
          </Col>

          {/* Private Campaigns Section */}
          {(isPrivateCampaignsLoading || privateCampaigns.length > 0) && (
            <Col span={24}>
              <CampaignList
                title={t('search.private_campaigns')}
                isLoading={isPrivateCampaignsLoading}
                campaigns={privateCampaigns}
                closeHandler={closeHandler}
              />
            </Col>
          )}

          {/* People to Follow Section */}
          {((isPeopleToFollowLoading || peopleToFollow.length > 0) && authKey) && (
            <Col span={24}>
              <UserList
                title={t('search.people_to_follow')}
                isLoading={isPeopleToFollowLoading}
                users={peopleToFollow}
                closeHandler={closeHandler}
                followHandler={effectiveFollowHandler}
              />
            </Col>
          )}
        </Row>
      )}

      {keyword !== '' && (
        <SearchFilter
          keyword={keyword}
          closeHandler={closeHandler}
          followHandler={effectiveFollowHandler}
        />
      )}
    </>
  )
}

export default Search;